import { LogLevel } from '@/modules/common/constants/logging.const';
import { loggerPreference } from '@/modules/common/services/logger-preference';
import logger from '@/modules/common/services/logger.service';
import { MyUser, Security } from '@/modules/common/models';
import { MarketTimeframes } from '@/modules/market-closed/types/market-closed';
import { AppState, newSessionState } from '@/store/store';
import { Mutation } from '@/types/vuex.custom';
import PermissionValidator from '@/utils/helpers/permissions';
import { ClientConfig, UXConfig, User2FASettings } from '@/utils/helpers/rest';
import { extend } from 'lodash';

/**
 * Export key names as a helper type
 */
export type MutationKeys = keyof typeof mutations;

export const mutations = {
  updateClientConfig: function (state, clientConfig) {
    state.clientConfig = clientConfig;

    // backend may enforce reload of the UI
    if (state.clientConfig.frontendHash !== '') {
      state.frontendHash = state.clientConfig.frontendHash;
    }

    if (!loggerPreference.hasPreference()) {
      if (clientConfig.systemProdLike) {
        // in production print only errors
        logger.setDebugLevel(LogLevel.Error);
      } else {
        // be verbose in development
        logger.setDebugLevel(LogLevel.Debug);
      }
    }
  } as Mutation<AppState, ClientConfig>,

  clearCurrentUser: function (state) {
    // @TODO solve issue of injecting initialConfig....if this is really necessary
    // could perhaps store initial state in the Store itself.
    // extend(state, newSessionState(initialConfig));
    extend(state, newSessionState());
  } as Mutation<AppState, void>,

  updateCurrentUser: function (state, user) {
    state.loginState.user = user;
    // cache permission validator as it is called often and user.roles will not change
    state.loginState.permissionValidator = new PermissionValidator(user.roles);
    // cache the effective trading permission of the user (might be restricted by the company's permissions)
    state.loginState.tradingPermissions =
      user.tradingPermissions === null
        ? user.companyTradingPermissions // inherit the company's permissions
        : user.tradingPermissions & user.companyTradingPermissions; // intersect company and user permissions;
  } as Mutation<AppState, MyUser>,

  updateUXConfig: function (state, uxConfig) {
    state.uxConfig = uxConfig;
    sessionStorage.uxConfig = JSON.stringify(uxConfig);
  } as Mutation<AppState, UXConfig>,

  update2FASettings: function (state, tfaSettings) {
    state.loginState.tfa = tfaSettings;
  } as Mutation<AppState, User2FASettings>,

  updateLenderLoanSocketEvent: function (state, loanId) {
    state.socketEvents.openLoans.lenderLoan = { loanId };
  } as Mutation<AppState, string>,

  updateBorrowerLoanSocketEvent: function (state, loanId) {
    state.socketEvents.openLoans.borrowerLoan = { loanId };
  } as Mutation<AppState, string>,

  updateBrokerLoanSocketEvent: function (state, loanId) {
    state.socketEvents.openLoans.brokerLoan = { loanId };
  } as Mutation<AppState, string>,

  updateTermLoanSocketEvent: function (state, termContractDisplayId) {
    state.socketEvents.termLoans = { termContractDisplayId };
  } as Mutation<AppState, string>,

  updateMarketplaceOrdersEvent: function (state, orderRef) {
    state.socketEvents.marketplace.orders = { orderRef };
  } as Mutation<AppState, string>,

  updateTermLoansEvent: function (state, orderRef) {
    state.socketEvents.termLoans = { termContractDisplayId: orderRef };
  } as Mutation<AppState, string>,

  updateLastVisitedSymbolOverview: function (state, security) {
    state.lastVisitedSymbolOverview = security;
  } as Mutation<AppState, Security>,

  updateMarketTimeframes: function (state, timeframes) {
    if (state.clientConfig) {
      state.clientConfig.marketTimeframes = timeframes;
    }
  } as Mutation<AppState, MarketTimeframes>,

  updateFrontendHash: function (state, hash: string) {
    state.frontendHash = hash;
    logger.debug('reconnect state.frontendHash=', state.frontendHash);
  } as Mutation<AppState, string>,

  refreshInstrumentPrice: function () {
    // this socket event is handled by a pinia store
    // we only implement the dummy mutation here to avoid a Vuex warning
  } as Mutation<AppState, void>,

  SENTRY_CONNECT: function (state, status) {
    state.sentryConnected = status;
  } as Mutation<AppState, boolean>,

  SOCKET_DISCONNECT: function (state) {
    state.socketConnected = false;
  } as Mutation<AppState, void>,

  SOCKET_ONOPEN: function (state) {
    logger.debug('ws: onopen', state);
    state.socketConnected = true;
    state.socketReconnecting = false;
  } as Mutation<AppState, void>,

  SOCKET_ONCLOSE: function (state) {
    state.socketConnected = false;
  } as Mutation<AppState, void>,

  SOCKET_ONERROR: function () {
    // ignore
  } as Mutation<AppState, void>,

  SOCKET_ONMESSAGE: function (_state, message) {
    // ignore
    logger.debug('ws: uncaught message...', message);
  } as Mutation<AppState, void>,

  SOCKET_RECONNECT: function (state, count) {
    logger.debug('ws: reconnecting...', count);
    state.socketReconnecting = true;
  } as Mutation<AppState, void>,

  SOCKET_RECONNECT_ERROR: function () {
    // ignore
  } as Mutation<AppState, void>,
};
